import { Stack } from '@fluentui/react';
import {
  type FC,
  type MouseEvent as ReactMouseEvent,
  type TransitionEvent as ReactTransitionEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTheme } from '@shared-legacy/ui-kit';
import { openSafeExternalUrl } from '@shared/lib/utils';

import { DocsDockControls } from './components/DocsDockControls';
import { PANEL_WIDTH_EASING, PANEL_WIDTH_TRANSITION_MS } from './constants';
import {
  getDocsBodyStackItemStyles,
  getDocsDockInnerStyle,
  getDocsDockOuterStyle,
  getDocsMainContentStackItemStyles,
  getDocsResizeHandleStyle,
  getDocsRootStackStyles,
  getDocsScrollAreaStyle,
  getDocsSidebarPanelStyle,
  getDocsSidebarStackStyles,
} from './styles/docsProvider.styles';
import type { DocsProviderProps } from './types';
import { DocsContext } from './useDocs';
import {
  applyDocsDockLayout,
  clampSidebarPanelWidth,
  clearDocsDockInlineLayout,
  computeDocsLayout,
  disableDocsDockTransitions,
} from './utils/layout';
import { prefersReducedMotion } from './utils/media';

export const DocsProvider: FC<DocsProviderProps> = ({ children }) => {
  const theme = useTheme();
  const [viewportWidthPx, setViewportWidthPx] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [isOpen, setIsOpen] = useState(false);
  const [activeDocUrl, setActiveDocUrl] = useState<string | null>(null);
  const [sidebarWidthPx, setSidebarWidthPx] = useState(() =>
    clampSidebarPanelWidth(400, typeof window !== 'undefined' ? window.innerWidth : 1200)
  );
  const [isResizing, setIsResizing] = useState(false);
  const docsBodyScrollRef = useRef<HTMLDivElement>(null);
  const dockOuterRef = useRef<HTMLDivElement>(null);
  const dockInnerRef = useRef<HTMLDivElement>(null);
  const sidebarPanelRef = useRef<HTMLDivElement>(null);
  const sidebarWidthRef = useRef(sidebarWidthPx);

  const layout = computeDocsLayout(viewportWidthPx, sidebarWidthPx, isOpen);
  const { isOverlayMode, dockWidthPx, innerWidthPx, innerMarginLeftPx } = layout;

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidthPx;
  }, [sidebarWidthPx]);

  const openDocs = useCallback(
    (url: string) => {
      setActiveDocUrl(url);
      setIsOpen(true);
      setSidebarWidthPx((w) => clampSidebarPanelWidth(w, viewportWidthPx));
    },
    [viewportWidthPx]
  );

  const closeDocs = useCallback(() => {
    setIsOpen(false);
    if (prefersReducedMotion()) {
      setActiveDocUrl(null);
    }
  }, []);

  const openActiveDocInNewTab = useCallback(() => {
    openSafeExternalUrl(activeDocUrl, '_blank', 'noopener,noreferrer');
  }, [activeDocUrl]);

  const setActiveDocUrlStable = useCallback((url: string) => {
    setActiveDocUrl(url);
  }, []);

  const handlePanelWidthTransitionEnd = useCallback(
    (event: ReactTransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      if (event.propertyName !== 'width') return;
      if (isOpen) return;
      setActiveDocUrl(null);
    },
    [isOpen]
  );

  useEffect(() => {
    const onResize = () => {
      const vw = window.innerWidth;
      setViewportWidthPx(vw);
      setSidebarWidthPx((w) => clampSidebarPanelWidth(w, vw));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onResizeHandleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsResizing(true);

      const startX = event.clientX;
      const startWidth = sidebarWidthRef.current;
      const dockElements = {
        outer: dockOuterRef.current,
        inner: dockInnerRef.current,
        sidebar: sidebarPanelRef.current,
      };
      const overlayBoxShadow = theme.palette?.black_a_8;
      let rafId = 0;

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      disableDocsDockTransitions(dockElements);
      const iframeEl = docsBodyScrollRef.current?.querySelector('iframe');
      if (iframeEl instanceof HTMLIFrameElement) {
        iframeEl.style.pointerEvents = 'none';
      }

      const applyWidth = (clientX: number) => {
        const viewportW = window.innerWidth;
        const nextWidth = clampSidebarPanelWidth(startWidth + (startX - clientX), viewportW);
        applyDocsDockLayout(dockElements, computeDocsLayout(viewportW, nextWidth, true), overlayBoxShadow);
      };

      const finishResize = (clientX: number) => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onMouseUp);
        document.body.style.removeProperty('user-select');
        document.body.style.removeProperty('cursor');

        const viewportW = window.innerWidth;
        const finalWidth = clampSidebarPanelWidth(startWidth + (startX - clientX), viewportW);
        clearDocsDockInlineLayout(dockElements);
        if (iframeEl instanceof HTMLIFrameElement) {
          iframeEl.style.pointerEvents = '';
        }
        setIsResizing(false);
        setSidebarWidthPx(finalWidth);
      };

      const onMove = (ev: MouseEvent) => {
        if (ev.buttons !== 1) {
          finishResize(ev.clientX);
          return;
        }
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          applyWidth(ev.clientX);
        });
      };

      const onMouseUp = (ev: MouseEvent) => {
        finishResize(ev.clientX);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [theme.palette?.black_a_8]
  );

  const panelWidthTransition =
    isResizing || prefersReducedMotion() ? 'none' : `width ${PANEL_WIDTH_TRANSITION_MS}ms ${PANEL_WIDTH_EASING}`;

  useLayoutEffect(() => {
    if (!isOpen || !activeDocUrl) return;
    const el = docsBodyScrollRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [activeDocUrl, isOpen]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      activeDocUrl,
      openDocs,
      closeDocs,
      setActiveDocUrl: setActiveDocUrlStable,
    }),
    [isOpen, activeDocUrl, openDocs, closeDocs, setActiveDocUrlStable]
  );

  return (
    <DocsContext.Provider value={contextValue}>
      <Stack horizontal verticalFill styles={getDocsRootStackStyles}>
        <Stack.Item grow styles={getDocsMainContentStackItemStyles}>
          {children}
        </Stack.Item>
        <div
          ref={dockOuterRef}
          onTransitionEnd={handlePanelWidthTransitionEnd}
          style={getDocsDockOuterStyle({
            dockWidthPx,
            isOverlayMode,
            isOpen,
            panelWidthTransition,
          })}
        >
          <div
            ref={dockInnerRef}
            style={getDocsDockInnerStyle({
              theme,
              innerWidthPx,
              innerMarginLeftPx,
              isOverlayMode,
              isOpen,
              isResizing,
            })}
          >
            {isOpen && activeDocUrl ? (
              <DocsDockControls onClose={closeDocs} onOpenInNewTab={openActiveDocInNewTab} />
            ) : null}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize documentation panel"
              title="Drag to resize"
              onMouseDown={onResizeHandleMouseDown}
              style={getDocsResizeHandleStyle(isOpen)}
            />
            <div
              ref={sidebarPanelRef}
              style={getDocsSidebarPanelStyle({ theme, sidebarWidthPx: layout.sidebarWidthPx })}
            >
              <Stack styles={getDocsSidebarStackStyles()}>
                <Stack.Item grow styles={getDocsBodyStackItemStyles}>
                  <div
                    ref={docsBodyScrollRef}
                    data-docs-scroll-root
                    style={{
                      ...getDocsScrollAreaStyle,
                      position: 'relative',
                    }}
                  >
                    {activeDocUrl ? (
                      <iframe
                        title="Documentation"
                        src={activeDocUrl}
                        allow="clipboard-write"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 0,
                          borderRadius: 0,
                          background: theme.palette?.white ?? '#ffffff',
                          pointerEvents: isResizing ? 'none' : 'auto',
                        }}
                      />
                    ) : null}
                  </div>
                </Stack.Item>
              </Stack>
            </div>
          </div>
        </div>
      </Stack>
    </DocsContext.Provider>
  );
};
